import { createContext, FC, PropsWithChildren, useContext, useMemo, useRef } from "react";

import { useFormContext, useWatch } from "react-hook-form";

import { ReferenceRateScheduleFields } from "../helpers/resolveScheduleDates";
import { RateData } from "../types";

import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";

const RateScheduleDigestContext = createContext<ReferenceRateScheduleFields[] | null>(null);
const RateNamesContext = createContext<string[] | null>(null);
const RateCategoryCountsContext = createContext<ReadonlyMap<string, number> | null>(null);

const useStableSlice = <T,>(value: T, signature: string): T => {
  const cache = useRef({ value, signature });
  if (cache.current.signature !== signature) {
    cache.current = { value, signature };
  }
  return cache.current.value;
};

export const RatesDigestProvider: FC<PropsWithChildren> = ({ children }) => {
  const { control } = useFormContext<StepTicketsFormData>();
  const rates = useWatch({ control, name: "rates" }) as RateData[] | undefined;

  const schedule = useMemo(() => {
    const digest = (rates ?? []).map((rate) => ({
      id: rate.id,
      isScheduled: rate.isScheduled,
      salesEndMode: rate.salesEndMode,
      salesEndDate: rate.salesEndDate,
    }));
    const signature = digest
      .map((rate) => `${rate.id}\u0001${rate.isScheduled}\u0001${rate.salesEndMode}\u0001${String(rate.salesEndDate)}`)
      .join("\u0002");
    return { digest, signature };
  }, [rates]);

  const names = useMemo(() => {
    const value = (rates ?? []).map((rate) => rate.name);
    return { value, signature: value.join("\u0002") };
  }, [rates]);

  const categoryCounts = useMemo(() => {
    const counts = (rates ?? [])
      .flatMap((rate) => rate.categoryIds ?? [])
      .reduce((accumulator, id) => accumulator.set(id, (accumulator.get(id) ?? 0) + 1), new Map<string, number>());
    const signature = [...counts].map(([id, count]) => `${id}\u0001${count}`).join("\u0002");
    return { counts, signature };
  }, [rates]);

  const stableSchedule = useStableSlice(schedule.digest, schedule.signature);
  const stableNames = useStableSlice(names.value, names.signature);
  const stableCategoryCounts = useStableSlice<ReadonlyMap<string, number>>(
    categoryCounts.counts,
    categoryCounts.signature
  );

  return (
    <RateScheduleDigestContext.Provider value={stableSchedule}>
      <RateNamesContext.Provider value={stableNames}>
        <RateCategoryCountsContext.Provider value={stableCategoryCounts}>{children}</RateCategoryCountsContext.Provider>
      </RateNamesContext.Provider>
    </RateScheduleDigestContext.Provider>
  );
};

export const useRateScheduleDigest = (): ReferenceRateScheduleFields[] => {
  const digest = useContext(RateScheduleDigestContext);
  if (!digest) {
    throw new Error("useRateScheduleDigest must be used within a RatesDigestProvider");
  }
  return digest;
};

export const useRateNames = (): string[] => {
  const names = useContext(RateNamesContext);
  if (!names) {
    throw new Error("useRateNames must be used within a RatesDigestProvider");
  }
  return names;
};

export const useRateCategoryCounts = (): ReadonlyMap<string, number> => {
  const counts = useContext(RateCategoryCountsContext);
  if (!counts) {
    throw new Error("useRateCategoryCounts must be used within a RatesDigestProvider");
  }
  return counts;
};
